import React, { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  BankAccountFormInput,
  BankAccountValidationSchema,
} from "@/components/pages/Settings/validation/bankAccountValidationSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mantine/core";
import { SettingsFormContent } from "@/components/pages/Settings";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { CreateAccount } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";

import CTA from "@/components/atoms/CTA";
import Space from "@/components/atoms/Space";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import TextInput from "@/components/atoms/TextInput";

interface DepartmentBankAccountFormProps {
  bankAccounts: CreateAccount[];
  currentCompanyId: number | undefined;
  isAdmin: boolean;
  addCompanyAccount: (address: CreateAccount) => void;
  removeCompanyAccount: (address: CreateAccount) => void;
}

const DepartmentBankAccountTable = ({
  bankAccounts,
  isAdmin,
  currentCompanyId,
  addCompanyAccount,
  removeCompanyAccount,
}: DepartmentBankAccountFormProps) => {
  const { t } = useTranslation();

  const [isAddEditBankAccountOpen, setIsAddEditBankAccountOpen] = useState(false);
  const [bankAccount, setBankAccount] = useState<CreateAccount | undefined>(undefined);

  const columns: ColumnDef<CreateAccount>[] = useMemo(
    () => [
      {
        header: t("settings:bankAccountName"),
        accessorKey: "name",
      },
      {
        header: t("settings:bankAccountNr"),
        accessorKey: "accountNo",
      },
      {
        header: "IBAN",
        accessorKey: "iban",
      },
    ],
    [t]
  );

  const removeAccount = async (account: CreateAccount) => {
    removeCompanyAccount(account);
  };

  const editActions = (account: CreateAccount) => [
    {
      label: t("settings:delete"),
      action: async () => {
        await removeAccount(account);
      },
    },
  ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Group justify="space-between">
          <Text size="md" strong={true} display="technical" tt="uppercase" data-testid="bank-details-heading">
            {t("settings:bankDetails")}
          </Text>
          <CTA
            disabled={!isAdmin}
            id="add-konto"
            intent="secondary"
            onClick={() => {
              setIsAddEditBankAccountOpen(true);
              setBankAccount(undefined);
            }}
            data-testid="add-new-account-btn"
          >
            {t("settings:newAccount")}
          </CTA>
        </Group>
        <Space height="25px" />
        <SettingsTable data={bankAccounts} columns={columns} isAdmin={isAdmin} editActions={editActions} />
      </SettingsFormContent>
      <EditOrAddBankAccountModal
        isOpen={isAddEditBankAccountOpen}
        setIsOpen={setIsAddEditBankAccountOpen}
        bankAccount={bankAccount}
        companyId={currentCompanyId}
        addCompanyAccount={addCompanyAccount}
      />
    </React.Fragment>
  );
};

export default DepartmentBankAccountTable;

interface EditOrAddBankAccountProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  bankAccount?: CreateAccount;
  companyId: number | undefined;
  addCompanyAccount: (address: CreateAccount) => void;
}

const EditOrAddBankAccountModal = ({
  isOpen,
  setIsOpen,
  bankAccount,
  companyId: _companyId,
  addCompanyAccount,
}: EditOrAddBankAccountProps) => {
  const ref = useRef<HTMLFormElement>(null);

  const { handleSubmit, control, reset } = useForm<BankAccountFormInput>({
    resolver: zodResolver(BankAccountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      name: bankAccount?.name || "",
      accountNo: bankAccount?.accountNo || "",
      iban: bankAccount?.iban || "",
    },
  });

  const onSubmit = async (data: BankAccountFormInput) => {
    addCompanyAccount({
      name: data.name,
      accountNo: data.accountNo,
      iban: data.iban,
    });
    reset();
    setIsOpen(false);
  };

  return (
    <Modal opened={isOpen} onClose={() => setIsOpen(!isOpen)} title={t("settings:addBankAcc")} size="lg">
      <form ref={ref} onSubmit={handleSubmit(onSubmit)} data-testid="add-bank-details-modal">
        <Grid p={20}>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  data-testid="bank-account-name-input"
                  label={t("settings:bankAccountName")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Controller
              control={control}
              name="accountNo"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  data-testid="bank-account-number-input"
                  label={t("settings:bankAccountNr")}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Controller
              control={control}
              name="iban"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  data-testid="bank-account-iban-input"
                  label={"IBAN"}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                />
              )}
            />
          </Grid.Col>
        </Grid>

        <ModalCTAs>
          <CTA id="add-bankAccount-cancel" intent="secondary" onClick={() => setIsOpen(!isOpen)}>
            {t("common:cancel")}
          </CTA>
          <CTA id="add-bankAccount-save" intent="primary" type="submit" data-testid={"save-bank-account-btn"}>
            {t("common:save")}
          </CTA>
        </ModalCTAs>
      </form>
    </Modal>
  );
};
